import type { ReactNode, SVGProps } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/*
 * Interface icons of the Figma set „Icons“ (4468:1101) and „Icon / sipka“ (4400:182), exported
 * from Figma as SVG. Content icons (cards, benefits, contact) are images in the administration.
 */

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const ICONS = {
  'arrow-right': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path d="M5 16H27" {...stroke} strokeWidth={2} />
        <path d="M18 7L27 16L18 25" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  'arrow-left': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path d="M27 16H5" {...stroke} strokeWidth={2} />
        <path d="M14 7L5 16L14 25" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  'arrow-up-right': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path d="M8 24L24 8" {...stroke} strokeWidth={2} />
        <path d="M11 8H24V21" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  /** Search field (no Figma icon): Phosphor „magnifying-glass“ regular on the 32 px grid. */
  'magnifying-glass': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <circle cx="14" cy="14" r="10" {...stroke} strokeWidth={2} />
        <path d="M21.07 21.07L28 28" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  /** Error state (no Figma icon): Phosphor „warning-circle“ regular on the 32 px grid. */
  'warning-circle': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <circle cx="16" cy="16" r="12" {...stroke} strokeWidth={2} />
        <path d="M16 10V17" {...stroke} strokeWidth={2} />
        <circle cx="16" cy="21.5" r="1.5" fill="currentColor" />
      </>
    ),
  },
  /** Password toggle, alerts, add buttons, attachments (no Figma icons): Phosphor regular, 32 px grid. */
  eye: {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path
          d="M3 16C6 10 10.5 7 16 7C21.5 7 26 10 29 16C26 22 21.5 25 16 25C10.5 25 6 22 3 16Z"
          {...stroke}
          strokeWidth={2}
        />
        <circle cx="16" cy="16" r="4" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  'eye-slash': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path d="M5 5L27 27" {...stroke} strokeWidth={2} />
        <path
          d="M13.2 8.2C14.1 7.4 15 7 16 7C21.5 7 26 10 29 16C28.2 17.7 27.2 19.1 26.1 20.3"
          {...stroke}
          strokeWidth={2}
        />
        <path
          d="M21.2 23.600C19.7 24.5 18 25 16 25C10.5 25 6 22 3 16C4.2 13.5 5.7 11.5 7.5 10"
          {...stroke}
          strokeWidth={2}
        />
        <path
          d="M13.2 13.200C12.5 13.9 12 14.9 12 16C12 18.2 13.8 20 16 20C17.1 20 18.1 19.5 18.8 18.8"
          {...stroke}
          strokeWidth={2}
        />
      </>
    ),
  },
  plus: {
    viewBox: '0 0 32 32',
    body: <path d="M16 6V26M6 16H26" {...stroke} strokeWidth={2} />,
  },
  info: {
    viewBox: '0 0 32 32',
    body: (
      <>
        <circle cx="16" cy="16" r="12" {...stroke} strokeWidth={2} />
        <path d="M16 15V22" {...stroke} strokeWidth={2} />
        <circle cx="16" cy="10.5" r="1.5" fill="currentColor" />
      </>
    ),
  },
  'file-text': {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path d="M8 4H19L26 11V28H8V4Z" {...stroke} strokeWidth={2} />
        <path d="M19 4V11H26M12 17H22M12 22H22" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  /** „Icon / sipka“ — arrow in buttons and links. */
  arrow: {
    viewBox: '0 0 24 24',
    body: <path d="M4 12H19M13.5 6.5L20 12L13.5 17.5" {...stroke} strokeWidth={1.75} />,
  },
  close: {
    viewBox: '0 0 24 24',
    body: <path d="M6 6L18 18M18 6L6 18" {...stroke} strokeWidth={2} />,
  },
  'caret-down': {
    viewBox: '0 0 16 16',
    body: <path d="M13 6L8 11L3 6" {...stroke} strokeWidth={1} />,
  },
  'chevron-left': {
    viewBox: '0 0 20 20',
    body: <path d="M12.5 4.5L7 10L12.5 15.5" {...stroke} strokeWidth={2} />,
  },
  'chevron-right': {
    viewBox: '0 0 20 20',
    body: <path d="M7.5 4.5L13 10L7.5 15.5" {...stroke} strokeWidth={2} />,
  },
  /** Autoplay control (no Figma icon): pause bars and play triangle in the chevron stroke. */
  pause: {
    viewBox: '0 0 20 20',
    body: <path d="M7 4.5V15.5M13 4.5V15.5" {...stroke} strokeWidth={2} />,
  },
  play: {
    viewBox: '0 0 20 20',
    body: <path d="M6.5 4.5L15 10L6.5 15.5V4.5Z" {...stroke} strokeWidth={2} />,
  },
  package: {
    viewBox: '0 0 32 32',
    body: (
      <path
        fill="currentColor"
        d="M27.96 8.26877L16.96 2.25002C16.6661 2.08763 16.3358 2.00244 16 2.00244C15.6642 2.00244 15.3339 2.08763 15.04 2.25002L4.04 8.27127C3.72586 8.44315 3.46363 8.69622 3.28069 9.00405C3.09775 9.31188 3.00081 9.66319 3 10.0213V21.9763C3.00081 22.3344 3.09775 22.6857 3.28069 22.9935C3.46363 23.3013 3.72586 23.5544 4.04 23.7263L15.04 29.7475C15.3339 29.9099 15.6642 29.9951 16 29.9951C16.3358 29.9951 16.6661 29.9099 16.96 29.7475L27.96 23.7263C28.2741 23.5544 28.5364 23.3013 28.7193 22.9935C28.9023 22.6857 28.9992 22.3344 29 21.9763V10.0225C28.9999 9.6638 28.9032 9.31172 28.7203 9.00317C28.5373 8.69462 28.2747 8.44096 27.96 8.26877ZM16 4.00002L26.0425 9.50002L22.3213 11.5375L12.2775 6.03752L16 4.00002ZM16 15L5.9575 9.50002L10.195 7.18002L20.2375 12.68L16 15ZM5 11.25L15 16.7225V27.4463L5 21.9775V11.25ZM27 21.9725L17 27.4463V16.7275L21 14.5388V19C21 19.2652 21.1054 19.5196 21.2929 19.7071C21.4804 19.8947 21.7348 20 22 20C22.2652 20 22.5196 19.8947 22.7071 19.7071C22.8946 19.5196 23 19.2652 23 19V13.4438L27 11.25V21.9713V21.9725Z"
      />
    ),
  },
  phone: {
    viewBox: '0 0 32 32',
    body: (
      <path
        d="M11.5595 15.6019C12.5968 17.7226 14.3158 19.4338 16.4412 20.4614C16.5967 20.5351 16.7687 20.567 16.9403 20.554C17.1119 20.541 17.2771 20.4836 17.4198 20.3874L20.5492 18.3006C20.6877 18.2083 20.8469 18.152 21.0126 18.1368C21.1782 18.1215 21.3451 18.1479 21.498 18.2134L27.3526 20.7225C27.5515 20.807 27.7175 20.9538 27.8257 21.1409C27.9339 21.328 27.9783 21.5451 27.9524 21.7596C27.7673 23.2076 27.0608 24.5385 25.9652 25.5031C24.8695 26.4678 23.4598 26.9999 22 27C17.4913 27 13.1673 25.2089 9.97919 22.0208C6.79107 18.8327 5 14.5087 5 10C5.00008 8.54022 5.53224 7.13052 6.49685 6.03485C7.46146 4.93918 8.79237 4.23267 10.2404 4.04763C10.4549 4.02167 10.672 4.06612 10.8591 4.1743C11.0461 4.28248 11.193 4.44852 11.2775 4.6474L13.7888 10.5071C13.8537 10.6587 13.8802 10.824 13.8658 10.9883C13.8514 11.1525 13.7967 11.3107 13.7064 11.4487L11.6268 14.6261C11.5322 14.7691 11.4762 14.9341 11.4644 15.1051C11.4526 15.2762 11.4854 15.4473 11.5595 15.6019Z"
        {...stroke}
        strokeWidth={2}
      />
    ),
  },
  question: {
    viewBox: '0 0 32 32',
    body: (
      <path
        fill="currentColor"
        d="M17.5 22.5C17.5 22.7967 17.412 23.0867 17.2472 23.3334C17.0824 23.58 16.8481 23.7723 16.574 23.8858C16.2999 23.9993 15.9983 24.0291 15.7074 23.9712C15.4164 23.9133 15.1491 23.7704 14.9393 23.5607C14.7296 23.3509 14.5867 23.0836 14.5288 22.7926C14.471 22.5017 14.5007 22.2001 14.6142 21.926C14.7277 21.6519 14.92 21.4176 15.1667 21.2528C15.4133 21.088 15.7033 21 16 21C16.3978 21 16.7794 21.158 17.0607 21.4393C17.342 21.7206 17.5 22.1022 17.5 22.5ZM16 9C13.2425 9 11 11.0188 11 13.5V14C11 14.2652 11.1054 14.5196 11.2929 14.7071C11.4804 14.8946 11.7348 15 12 15C12.2652 15 12.5196 14.8946 12.7071 14.7071C12.8946 14.5196 13 14.2652 13 14V13.5C13 12.125 14.3463 11 16 11C17.6538 11 19 12.125 19 13.5C19 14.875 17.6538 16 16 16C15.7348 16 15.4804 16.1054 15.2929 16.2929C15.1054 16.4804 15 16.7348 15 17V18C15 18.2652 15.1054 18.5196 15.2929 18.7071C15.4804 18.8946 15.7348 19 16 19C16.2652 19 16.5196 18.8946 16.7071 18.7071C16.8946 18.5196 17 18.2652 17 18V17.91C19.28 17.4913 21 15.6725 21 13.5C21 11.0188 18.7575 9 16 9ZM29 16C29 18.5712 28.2376 21.0846 26.8091 23.2224C25.3807 25.3603 23.3503 27.0265 20.9749 28.0104C18.5995 28.9944 15.9856 29.2518 13.4638 28.7502C10.9421 28.2486 8.6257 27.0105 6.80762 25.1924C4.98953 23.3743 3.75141 21.0579 3.2498 18.5362C2.74819 16.0144 3.00563 13.4006 3.98957 11.0251C4.97351 8.64968 6.63975 6.61935 8.77759 5.1909C10.9154 3.76244 13.4288 3 16 3C19.4467 3.00364 22.7512 4.37445 25.1884 6.81163C27.6256 9.24882 28.9964 12.5533 29 16ZM27 16C27 13.8244 26.3549 11.6977 25.1462 9.88873C23.9375 8.07979 22.2195 6.66989 20.2095 5.83733C18.1995 5.00476 15.9878 4.78692 13.854 5.21136C11.7202 5.6358 9.76021 6.68345 8.22183 8.22183C6.68345 9.7602 5.63581 11.7202 5.21137 13.854C4.78693 15.9878 5.00477 18.1995 5.83733 20.2095C6.66989 22.2195 8.07979 23.9375 9.88873 25.1462C11.6977 26.3549 13.8244 27 16 27C18.9164 26.9967 21.7123 25.8367 23.7745 23.7745C25.8367 21.7123 26.9967 18.9164 27 16Z"
      />
    ),
  },
  envelope: {
    viewBox: '0 0 32 32',
    body: (
      <>
        <path d="M28 7L16 18L4 7" {...stroke} strokeWidth={2} />
        <path
          d="M4 7H28V24C28 24.27 27.89 24.52 27.71 24.71C27.52 24.89 27.27 25 27 25H5C4.73 25 4.48 24.89 4.29 24.71C4.11 24.52 4 24.27 4 24V7Z"
          {...stroke}
          strokeWidth={2}
        />
        <path d="M13.82 16L4.31 24.72" {...stroke} strokeWidth={2} />
        <path d="M27.69 24.72L18.18 16" {...stroke} strokeWidth={2} />
      </>
    ),
  },
  'map-pin': {
    viewBox: '0 0 32 32',
    body: (
      <path
        fill="currentColor"
        d="M27 26H17V16.93C18.75 16.67 20.34 15.77 21.45 14.39C22.56 13.01 23.11 11.26 22.98 9.5C22.86 7.73 22.06 6.08 20.77 4.88C19.47 3.67 17.77 3 16 3C14.23 3 12.53 3.67 11.23 4.88C9.94 6.08 9.14 7.73 9.02 9.5C8.89 11.26 9.44 13.01 10.55 14.39C11.66 15.77 13.25 16.67 15 16.93V26H5C4.73 26 4.48 26.11 4.29 26.29C4.11 26.48 4 26.73 4 27C4 27.27 4.11 27.52 4.29 27.71C4.48 27.89 4.73 28 5 28H27C27.27 28 27.52 27.89 27.71 27.71C27.89 27.52 28 27.27 28 27C28 26.73 27.89 26.48 27.71 26.29C27.52 26.11 27.27 26 27 26ZM11 10C11 9.01 11.29 8.04 11.84 7.22C12.39 6.4 13.17 5.76 14.09 5.38C15 5 16.01 4.9 16.98 5.1C17.95 5.29 18.84 5.77 19.54 6.46C20.23 7.16 20.71 8.05 20.9 9.02C21.1 9.99 21 11 20.62 11.91C20.24 12.83 19.6 13.61 18.78 14.16C17.96 14.71 16.99 15 16 15C14.67 15 13.4 14.47 12.46 13.54C11.53 12.6 11 11.33 11 10Z"
      />
    ),
  },
  'seal-check': {
    viewBox: '0 0 32 32',
    body: (
      <path
        fill="currentColor"
        d="M28.23 12.85C27.76 12.36 27.27 11.85 27.09 11.41C26.92 11 26.91 10.32 26.9 9.66C26.88 8.44 26.86 7.06 25.9 6.1C24.94 5.14 23.56 5.12 22.34 5.1C21.68 5.09 21 5.08 20.59 4.91C20.15 4.73 19.64 4.24 19.15 3.77C18.29 2.94 17.31 2 16 2C14.7 2 13.72 2.94 12.85 3.77C12.36 4.24 11.85 4.73 11.41 4.91C11 5.08 10.32 5.09 9.66 5.1C8.44 5.12 7.06 5.14 6.1 6.1C5.14 7.06 5.13 8.44 5.1 9.66C5.09 10.32 5.08 11 4.91 11.41C4.73 11.85 4.24 12.36 3.77 12.85C2.94 13.72 2 14.7 2 16C2 17.31 2.94 18.28 3.77 19.15C4.24 19.64 4.73 20.15 4.91 20.59C5.08 21 5.09 21.68 5.1 22.34C5.12 23.56 5.14 24.94 6.1 25.9C7.06 26.86 8.44 26.88 9.66 26.9C10.32 26.91 11 26.92 11.41 27.09C11.85 27.27 12.36 27.76 12.85 28.23C13.72 29.06 14.7 30 16 30C17.31 30 18.28 29.06 19.15 28.23C19.64 27.76 20.15 27.27 20.59 27.09C21 26.92 21.68 26.91 22.34 26.9C23.56 26.88 24.94 26.86 25.9 25.9C26.86 24.94 26.88 23.56 26.9 22.34C26.91 21.68 26.92 21 27.09 20.59C27.27 20.15 27.76 19.64 28.23 19.15C29.06 18.29 30 17.31 30 16C30 14.7 29.06 13.72 28.23 12.85ZM26.79 17.76C26.19 18.39 25.57 19.04 25.24 19.83C24.93 20.59 24.91 21.46 24.9 22.31C24.89 23.18 24.87 24.1 24.49 24.49C24.1 24.87 23.19 24.89 22.31 24.9C21.46 24.91 20.59 24.93 19.83 25.24C19.04 25.57 18.39 26.19 17.76 26.79C17.14 27.39 16.5 28 16 28C15.5 28 14.86 27.39 14.24 26.79C13.62 26.19 12.97 25.57 12.17 25.24C11.41 24.93 10.54 24.91 9.69 24.9C8.82 24.89 7.9 24.87 7.52 24.49C7.13 24.1 7.11 23.19 7.1 22.31C7.09 21.46 7.07 20.59 6.76 19.83C6.43 19.04 5.81 18.39 5.21 17.76C4.61 17.14 4 16.5 4 16C4 15.5 4.62 14.86 5.21 14.24C5.81 13.62 6.43 12.97 6.76 12.17C7.07 11.41 7.09 10.54 7.1 9.69C7.11 8.82 7.13 7.9 7.52 7.52C7.9 7.13 8.81 7.11 9.69 7.1C10.54 7.09 11.41 7.07 12.17 6.76C12.97 6.43 13.61 5.81 14.24 5.21C14.86 4.61 15.5 4 16 4C16.5 4 17.14 4.62 17.76 5.21C18.38 5.81 19.04 6.43 19.83 6.76C20.59 7.07 21.46 7.09 22.31 7.1C23.18 7.11 24.1 7.13 24.49 7.52C24.87 7.9 24.89 8.81 24.9 9.69C24.91 10.54 24.93 11.41 25.24 12.17C25.57 12.97 26.19 13.61 26.79 14.24C27.39 14.86 28 15.5 28 16C28 16.5 27.39 17.14 26.79 17.76ZM21.71 12.29C21.8 12.39 21.87 12.5 21.92 12.62C21.97 12.74 22 12.87 22 13C22 13.13 21.97 13.26 21.92 13.38C21.87 13.5 21.8 13.61 21.71 13.71L14.71 20.71C14.61 20.8 14.5 20.87 14.38 20.92C14.26 20.97 14.13 21 14 21C13.87 21 13.74 20.97 13.62 20.92C13.5 20.87 13.39 20.8 13.29 20.71L10.29 17.71C10.1 17.52 10 17.27 10 17C10 16.73 10.1 16.48 10.29 16.29C10.48 16.1 10.73 16 11 16C11.27 16 11.52 16.1 11.71 16.29L14 18.59L20.29 12.29C20.39 12.2 20.5 12.13 20.62 12.08C20.74 12.03 20.87 12 21 12C21.13 12 21.26 12.03 21.38 12.08C21.5 12.13 21.61 12.2 21.71 12.29Z"
      />
    ),
  },
  clock: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeMiterlimit={10}
        />
        <path d="M12 6.75V12H17.25" {...stroke} strokeWidth={1.5} />
      </>
    ),
  },
  /** Filling section link icons (Figma 4704:4177, 4704:4182). */
  download: {
    viewBox: '0 0 24 24',
    body: <path d="M12 4V15M16.5 10.5L12 15L7.5 10.5M5 19.5H19" {...stroke} strokeWidth={1.5} />,
  },
  'play-circle': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25Z"
          {...stroke}
          strokeWidth={1.5}
        />
        <path d="M10.5 9.25V14.75L15 12L10.5 9.25Z" {...stroke} strokeWidth={1.5} />
      </>
    ),
  },
} satisfies Record<string, { viewBox: string; body: ReactNode }>;

export type IconName = keyof typeof ICONS;

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox'> & {
  name: IconName;
};

/** Decorative interface icon; size and color come from `className` (`size-6 text-[#2c7a25]`). */
export function Icon({ name, className, ...props }: IconProps) {
  const icon = ICONS[name];

  return (
    <svg
      viewBox={icon.viewBox}
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      {...props}
    >
      {icon.body}
    </svg>
  );
}
